/**
 * React Query hook for Billing Summary
 * Provides automatic caching, background sync, and error handling
 * Follows the same pattern as LeadsV2 queries
 */

import { useQuery } from '@tanstack/react-query';
import { getCaseBillingSummary } from 'services/CaseServices';
import { queryKeys } from '../../../config/queryClient';

/**
 * Fetches billing summary data
 *
 * @param {Object} options - Query options
 * @param {string} options.caseId - Optional case ID to filter summary (default: '')
 * @param {boolean} options.excludeFromLoading - Exclude from global loading state (default: true)
 * @returns {Promise<Object|null>} - Billing summary object or null
 */
const fetchBillingSummary = async ({ caseId = '', excludeFromLoading = true }) => {
  try {
    const response = await getCaseBillingSummary({ case_id: caseId }, excludeFromLoading);

    if (response?.data && response?.data.length > 0) {
      return response.data[0];
    }

    return null;
  } catch (error) {
    console.error('Error fetching billing summary:', error);
    throw error;
  }
};

/**
 * Hook to fetch billing summary with React Query
 *
 * @param {Object} options - Configuration options
 * @param {string} options.caseId - Optional case ID to filter summary (default: '')
 * @param {boolean} options.excludeFromLoading - Exclude from global loading state (default: true)
 * @param {boolean} options.enabled - Enable/disable query (default: true)
 *
 * @returns {Object} - billingSummary, isLoading, error, refetch
 */
export const useBillingSummaryQuery = ({ caseId = '', excludeFromLoading = true, enabled = true } = {}) => {
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: queryKeys.billing.summary(caseId),
    queryFn: () => fetchBillingSummary({ caseId, excludeFromLoading }),
    enabled,
    staleTime: 10 * 60 * 1000, // Consider data stale after 10 minutes
    cacheTime: 15 * 60 * 1000, // Keep in cache for 15 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus for relatively static data
    retry: 2, // Retry twice on failure
  });

  return {
    billingSummary: data || null,
    isLoading,
    isFetching,
    error,
    refetch,
  };
};
